import { lentComponent, useContractMutation, useContractQuery, useInvalidateContract } from "@amtana/browser";
import type { LentToken, MutationResult, QueryResult } from "@amtana/browser";
import { prospectTrpcContract } from "@amtana/prospect-contract";
import type {
  ChangedCount,
  CountMatching,
  ProspectScope,
  ListProspectsInput,
  Prospect,
  ProspectPage,
  RemoveProspectsInput,
  UpdateProspectsInput,
} from "@amtana/prospect-contract";

// Prospect data for every browser module, and the prospect table it lends.

export type ProspectTableProps = {
  /** Only this analysis's prospects; all of them when absent. */
  readonly analysisId?: string;
  /** A filter's scope: source analyses and conditions. */
  readonly scope?: ProspectScope;
  /** Refresh while the source is still extracting. */
  readonly live?: boolean;
  /** What an export from this table is called and where it comes from. */
  readonly exportSource?: {
    readonly kind: "analysis" | "list" | "prospects";
    readonly id: string | null;
    readonly name: string;
  };
};

export const ProspectTable: LentToken<ProspectTableProps> = lentComponent<ProspectTableProps>(
  "prospect",
  "table",
);

/** The home's "Latest prospects" column (S39.01): the newest five, each opening its prospect. */
export const LatestProspects: LentToken<Record<string, never>> = lentComponent<Record<string, never>>(
  "prospect",
  "LatestProspects",
);

/**
 * The organization's newest prospects (S39.01), for the home: the list's first page, which is
 * newest first, cut to `limit`. `optional`: the home goes on without them.
 */
export function useRecentProspects(limit: number): QueryResult<ProspectPage> {
  return useContractQuery(prospectTrpcContract, "list", {
    input: { page: 1, pageSize: limit },
    optional: true,
  });
}

export function useProspects(
  input: ListProspectsInput,
  options: { live?: boolean } = {},
): QueryResult<ProspectPage> {
  return useContractQuery(prospectTrpcContract, "list", {
    input,
    refetchInterval: options.live ? 2500 : false,
  });
}

/**
 * One prospect. `optional` when it is a side panel (a contact beside a
 * conversation, a drawer over a table): its failure stays on the result
 * instead of replacing the screen (packages/module ADR-001, point 6).
 */
export function useProspect(
  prospectId: string | undefined,
  options: { readonly optional?: boolean } = {},
): QueryResult<Prospect> {
  return useContractQuery(prospectTrpcContract, "get", {
    input: { prospectId: prospectId ?? "" },
    enabled: prospectId !== undefined,
    ...(options.optional ? { optional: true } : {}),
  });
}

export function useUpdateProspects(): MutationResult<ChangedCount, UpdateProspectsInput> {
  const mutation = useContractMutation(prospectTrpcContract, "update");
  const invalidate = useInvalidateContract();
  return {
    ...mutation,
    run: (input: UpdateProspectsInput) =>
      mutation.mutateAsync(input).finally(() => void invalidate(prospectTrpcContract)),
  };
}

export function useRemoveProspects(): MutationResult<ChangedCount, RemoveProspectsInput> {
  const mutation = useContractMutation(prospectTrpcContract, "remove");
  const invalidate = useInvalidateContract();
  return {
    ...mutation,
    run: (input: RemoveProspectsInput) => mutation.mutateAsync(input).finally(() => void invalidate()),
  };
}

/** How many prospects a scope holds: a filter's live preview. */
export function useCountMatching(
  scope: ProspectScope,
  options: { enabled?: boolean } = {},
): QueryResult<CountMatching> {
  return useContractQuery(prospectTrpcContract, "countMatching", {
    input: scope,
    enabled: options.enabled ?? true,
  });
}
